import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import arrayToTree from 'array-to-tree';
import moment from 'moment';
import { IResMenu } from 'api/services/menus/types';
import { menuItemServices } from 'api/services';
import { IMenuItem, IResMenuItem } from 'api/services/menuItems/types';
import { menuServices } from 'api/services';
import handleNotification from 'helpers/handleNotification';
import Confirmation from 'shared/Confirmation/Confirmation';
import CustomDND from 'shared/CustomDND/CustomDND';
import CustomModal from 'shared/CustomModal/CustomModal';
import { MainTemplate } from 'shared/MainTemplate/MainTemplate';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import { AppState } from 'store/store';
import { useSelector } from 'react-redux';
import Form from 'shared/Form/Form';
import { fields, view } from './formData';
import { SERVER_URL } from 'constants/project';
import 'react-nestable/dist/styles/index.css';
import './MenuItemPage.sass';

export const MenuItemPage: React.FC = () => {
	const { menuId } = useParams<{ menuId: string }>();
	const { currentLang } = useSelector((state: AppState) => state.langReducer);
	const [activeMenuItem, setActiveMenuItem] = useState<IResMenuItem>();
	const [menuItems, setMenuItems] = useState<IResMenuItem[]>([]);
	const [loading, setLoading] = useState(true);
	const confirmationRef = useRef<any>(null);
	const createModalRef = useRef<any>();
	const updateModalRef = useRef<any>();
	const createFormRef = useRef<any>();
	const updateFormRef = useRef<any>();

	useEffect(() => {
		if (currentLang?.code) getMenu();
	}, [currentLang?.code]);

	const buildTree = async (menuItems: IResMenuItem[]) => {
		const tree = await arrayToTree(menuItems, {
			parentProperty: 'parentId',
			customID: 'id',
			childrenProperty: 'children',
		});
		setMenuItems(tree);
	};

	const getMenu = async () => {
		const res = await menuServices.getMenuById(menuId).then((res) => res.data);
		buildTree(res.menuItems);
		setLoading(false);
	};

	const createMenuItem = async () => {
		const formRes = createFormRef.current?.submit();
		console.log(formRes?.res.taxonomyId);
		if (!formRes || formRes.hasErrors) {
			handleNotification('Заполните поля');
			return;
		}
		const {
			label,
			link,
			newWindow,
			postId,
			pageId,
			taxonomyId,
			postCategoryId,
			type,
			fileId,
			image,
		} = formRes.res;

		const requestData: IMenuItem = {
			label,
			link,
			newWindow,
			menuId: Number(menuId),
			fileId,
			postId,
			pageId,
			taxonomyId,
			postCategoryId,
			type,
			image,
		};
		try {
			await menuItemServices.createMenuItem(requestData);
			handleNotification('Сохранено', 'success');
			getMenu();
			createModalRef.current.onHide();
		} catch (e: any) {
			handleNotification(e?.response.data.message || 'Ошибка');
		}
	};

	const updateMenuItem = async () => {
		if (!activeMenuItem) return;

		const formRes = updateFormRef.current?.submit();
		if (!formRes || formRes.hasErrors) {
			handleNotification('Заполните поля');
			return;
		}
		const {
			label,
			link,
			newWindow,
			postId,
			pageId,
			taxonomyId,
			postCategoryId,
			type,
			fileId,
			image,
		} = formRes.res;

		const requestData: IMenuItem = {
			label,
			link,
			newWindow,
			menuId: Number(menuId),
			fileId,
			postId,
			pageId,
			taxonomyId,
			postCategoryId,
			type,
			image,
		};
		try {
			await menuItemServices.updateMenuItem(activeMenuItem.id, requestData);
			handleNotification('Меню обновлено!', 'success');
			getMenu();
			updateModalRef.current.onHide();
		} catch (e: any) {
			handleNotification(e?.response.data.message || 'Ошибка');
		}
	};

	const deleteMenuItem = async () => {
		if (!activeMenuItem) return;
		await menuItemServices.deleteMenuItem(activeMenuItem.id);
		handleNotification('Меню успешно удалено!', 'success');
		getMenu();
	};

	const renderItem = ({ item, collapseIcon }: any) => (
		<ul className='menu-item-page__list nestable__list'>
			<li className='nestable__text menu-item-page__text--padding'>
				{collapseIcon} {item.label}
			</li>
			<li className='nestable__text menu-item-page__text--padding'>
				{item.link}
			</li>
			<li className='nestable__text'>{item.newWindow ? 'Да' : 'Нет'}</li>
			<li className='nestable__image'>
				{item.image && (
					<img src={`${SERVER_URL}/${item.image}`} alt={item.file?.alt || ''} />
				)}
			</li>
			<li className='nestable__text'>
				{moment(item.updatedAt).format('DD.MM.YYYY HH:mm')}
			</li>
			<li className='table-last-item'>
				<EditIcon
					onClick={() => {
						setActiveMenuItem(item);
						updateModalRef.current?.onShow();
					}}
					className='table-icon'
				/>
				<DeleteForeverIcon
					onClick={() => {
						setActiveMenuItem(item);
						confirmationRef.current.onShow();
					}}
					color='secondary'
					className='table-icon'
				/>
			</li>
		</ul>
	);

	return (
		<MainTemplate
			title='Пункты меню'
			handler={() => createModalRef.current?.onShow()}>
			<div className='menu-items__container'>
				<ul className='nestable__list menu-item-page__list'>
					<li className='nestable__text MuiTableCell-head'>Название</li>
					<li className='nestable__text MuiTableCell-head'>Ссылка</li>
					<li className='nestable__text MuiTableCell-head'>Новое окно</li>
					<li className='nestable__text MuiTableCell-head'>Изображение</li>
					<li className='nestable__text MuiTableCell-head'>Дата изменения</li>
					<li className='nestable__text MuiTableCell-head table-last-item'>
						Действие
					</li>
				</ul>
				<CustomDND
					items={menuItems}
					renderItems={renderItem}
					submitDrag={menuItemServices.changeMenuItemOrder}
					refetchData={getMenu}
				/>
			</div>

			<Confirmation
				ref={confirmationRef}
				text='Вы действительно хотите удалить этот пункт?'
				submit={deleteMenuItem}
				title='Удаление пункта меню'
				load={loading}
				changeState={() => setActiveMenuItem(undefined)}
			/>
			<CustomModal
				ref={createModalRef}
				submit={createMenuItem}
				title='Создание пункта меню'
				text='Добавить'
				changeState={() => setActiveMenuItem(undefined)}>
				<Form fields={fields} view={view} ref={createFormRef} />
			</CustomModal>
			<CustomModal
				ref={updateModalRef}
				submit={updateMenuItem}
				title='Редактирование пункта меню'
				text='Редактировать'
				changeState={() => setActiveMenuItem(undefined)}>
				<Form
					fields={fields}
					view={view}
					initialData={activeMenuItem}
					hasInitialData
					ref={updateFormRef}
				/>
			</CustomModal>
		</MainTemplate>
	);
};
